import { MouseEvent, useEffect, useMemo, useState } from 'react'
import buildQuery from 'odata-query'
import { ServiceConfig } from '@user/store/services/config'
import { get } from '@user/store/services/crud'
import { useQuery } from 'react-query'
import { useDebounce } from '@user/utils/useDebounce'
import { useTablePagination } from '@sales-center/components'
import {
  ENTITY_TYPES,
  escapeODataString,
  ODataResponse,
  removeEmptyProps,
} from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks'
import { setWorkWaveContactDuplicates } from '@user/store/duplicates/WorkwaveContacts/slice'

export interface Duplicate {
  id: number
  entityType: ENTITY_TYPES
  entityName: string
  firstName: string
  lastName: string
  company: string
  address: string
  city: string
  state: string
  postalCode: string
  emailAddress: string
  phone: string
}

export type DuplicateCheckResponse = {
  loading: boolean
  resultCount: number
  duplicates: Duplicate[]
  pageCount: number
  page: number
  perPage: number
  onChangePage: (event: MouseEvent<unknown> | null, newPage: number) => void
}

const baseUrl = `${ServiceConfig.baseAPIUrl}/odata/duplicateCheck`

const fetchDupes = async (
  filterObj: Record<string, string>,
  page: number,
  perPage: number,
  orderBy?: string,
  searchText?: string,
): Promise<ODataResponse<Duplicate>> => {
  // clone so we can mutate
  const filter = { ...filterObj }

  // no dupes if no input
  if (Object.keys(filter).length === 0) return { '@odata.count': 0, value: [] }

  const skip = page * perPage

  const hasFullName = !!filter.firstName && !!filter.lastName

  const fullNameFilter = hasFullName
    ? `(firstName eq '${escapeODataString(filter.firstName)}' and lastName eq '${escapeODataString(filter.lastName)}')`
    : ''

  if (hasFullName) {
    delete filter.firstName
    delete filter.lastName
  }

  const filterStrings = Object.keys(filter).map<string>(
    (field) => `${field} eq '${escapeODataString(filter[field])}'`,
  )

  filterStrings.push(fullNameFilter)

  const searchFilters: string[] = []

  if (searchText) {
    const searchableFields = [
      'firstName',
      'lastName',
      'company',
      'address',
      'city',
      'state',
      'postalCode',
      'emailAddress',
      'phone',
      'entityName',
      'entityType',
    ]
    const usedFields = Object.keys(filter)
    const fieldsToSearch = searchableFields.filter(
      (field) => !usedFields.includes(field),
    )
    if (fieldsToSearch.length > 0) {
      // URL encode search text to handle special characters in contains() function
      searchFilters.push(
        `(${fieldsToSearch.map((field) => `contains(${field}, '${escapeODataString(searchText)}')`).join(' or ')})`,
      )
    }
  }

  const qstring = buildQuery({
    filter: {
      or: [...filterStrings],
      and: searchFilters.length > 0 ? searchFilters : undefined,
    },
    skip,
    top: perPage,
    count: page === 0,
    orderBy: orderBy || 'id desc',
  })

  const response = await get(`${baseUrl}${qstring}`)
  if (response.ok) {
    const result: ODataResponse<Duplicate> = await response.json()
    result.value.map((d) => {
      d.entityType =
        d.entityType.toString() == 'Lead'
          ? ENTITY_TYPES.Lead
          : ENTITY_TYPES.Opportunity
    })
    return result
  }
  return { '@odata.count': 0, value: [] }
}

export const useDuplicateCheck = (
  filter: Record<string, string>,
  perPage: number,
  debounceDelay: number = 500,
  page: number = 1,
  orderBy: string = 'id desc',
  searchText: string = '',
): DuplicateCheckResponse => {
  const dispatch = useAppDispatch()

  const debounced = useDebounce(removeEmptyProps(filter), debounceDelay)
  const { onChangePage } = useTablePagination()

  const { isFetching: loading, data } = useQuery(
    ['duplicateCheck', debounced, page, perPage, orderBy, searchText],
    () => fetchDupes(debounced, page - 1, perPage, orderBy, searchText),
    { keepPreviousData: true },
  )

  const [resultCount, setResultCount] = useState(0)

  useEffect(() => {
    if (!!data && data['@odata.count'] !== undefined) {
      setResultCount(data['@odata.count'])
    }
  }, [data])

  const duplicates = data?.value ?? []

  const pageCount = useMemo(
    () => Math.ceil(resultCount / perPage),
    [perPage, resultCount],
  )

  useEffect(() => {
    if (data) {
      dispatch(
        setWorkWaveContactDuplicates({
          loading,
          resultCount,
          duplicates,
          pageCount,
          page,
          perPage,
          onChangePage,
          orderBy,
          searchText,
        }),
      )
    }
  }, [
    data,
    dispatch,
    duplicates,
    loading,
    onChangePage,
    page,
    pageCount,
    resultCount,
    perPage,
    orderBy,
    searchText,
  ])

  return {
    loading,
    resultCount,
    duplicates,
    pageCount,
    page,
    perPage,
    onChangePage,
  }
}
